import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useState, useEffect } from 'react';
import { Building2, Mail, Phone, QrCode, FileText } from 'lucide-react';
import { PixPayment } from '@/components/payment/PixPayment';
import { BoletoPayment } from '@/components/payment/BoletoPayment';
import { maskNameLGPD, maskTaxIdLGPD, maskEmailLGPD, maskPhoneLGPD } from '@/lib/maskUtils';

interface Invoice {
  id: string;
  amount: number;
  due_date: string;
  status: string;
  description: string | null;
  pix_code: string | null;
  boleto_barcode: string | null;
  boleto_pdf_url: string | null;
  customer: {
    name: string;
    tax_id: string;
    email: string | null;
    phone: string | null;
  };
  company: {
    name: string;
    trade_name: string | null;
    tax_id: string;
    logo_url: string | null;
    pix_key: string | null;
  };
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value / 100);
}

function maskTaxId(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  } else if (digits.length === 14) {
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return value;
}

const PaymentPage = () => {
  const { invoiceId, linkToken } = useParams();
  const [paymentMethod, setPaymentMethod] = useState<'pix' | 'boleto'>('pix');

  // Track link click
  const trackMutation = useMutation({
    mutationFn: async () => {
      if (linkToken) {
        await supabase
          .from('message_links')
          .update({ clicked_at: new Date().toISOString() })
          .eq('link_token', linkToken);
      }
    }
  });

  useEffect(() => {
    if (linkToken) {
      trackMutation.mutate();
    }
  }, [linkToken]);

  const { data: invoice, isLoading, error } = useQuery({
    queryKey: ['payment-invoice', invoiceId, linkToken],
    queryFn: async () => {
      let invoiceIdToFetch = invoiceId;

      // If we have a link token, resolve it first
      if (linkToken && !invoiceId) {
        const { data: link } = await supabase
          .from('message_links')
          .select('invoice_id')
          .eq('link_token', linkToken)
          .single();
        
        if (link) {
          invoiceIdToFetch = link.invoice_id;
        }
      }

      if (!invoiceIdToFetch) throw new Error('Fatura não encontrada');

      const { data, error } = await supabase
        .from('invoices')
        .select(`
          *,
          customer:customers!inner(name, tax_id, email, phone),
          company:companies!inner(name, trade_name, tax_id, logo_url, pix_key)
        `)
        .eq('id', invoiceIdToFetch)
        .single();

      if (error) throw error;
      return data as unknown as Invoice;
    }
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[hsl(var(--basic-50))] to-[hsl(var(--basic-100))] flex items-center justify-center">
        <div className="text-muted-foreground">Carregando...</div>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[hsl(var(--basic-50))] to-[hsl(var(--basic-100))] flex items-center justify-center p-4">
        <div className="bg-card rounded-2xl shadow-xl p-8 text-center max-w-md">
          <h1 className="text-xl font-bold text-[hsl(var(--careful-600))] mb-2">Fatura não encontrada</h1>
          <p className="text-muted-foreground">O link de pagamento pode estar expirado ou inválido.</p>
        </div>
      </div>
    );
  }

  const isPaid = invoice.status === 'paid';

  return (
    <div className="min-h-screen bg-gradient-to-br from-[hsl(var(--basic-50))] to-[hsl(var(--basic-100))]">
      {/* Header with Company Branding */}
      <header className="bg-card border-b">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {invoice.company.logo_url ? (
              <img 
                src={invoice.company.logo_url} 
                alt={invoice.company.name}
                className="h-10 w-10 object-contain rounded-lg"
              />
            ) : (
              <div className="h-10 w-10 rounded-lg bg-[hsl(var(--basic-100))] flex items-center justify-center">
                <Building2 className="h-5 w-5 text-[hsl(var(--basic-500))]" />
              </div>
            )}
            <div>
              <p className="font-bold text-[hsl(var(--basic-900))]">
                {invoice.company.trade_name || invoice.company.name}
              </p>
              <p className="text-xs text-muted-foreground">
                CNPJ: {maskTaxId(invoice.company.tax_id)}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <a href={`mailto:suporte@${invoice.company.name.toLowerCase().replace(/\s/g, '')}.com`} 
               className="p-2 rounded-lg hover:bg-muted transition-colors">
              <Mail className="h-5 w-5 text-muted-foreground" />
            </a>
            <a href={`https://wa.me/5511999999999`} target="_blank" rel="noopener noreferrer"
               className="p-2 rounded-lg hover:bg-muted transition-colors">
              <Phone className="h-5 w-5 text-muted-foreground" />
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {isPaid ? (
          <div className="bg-[hsl(var(--accent-50))] border-2 border-[hsl(var(--accent-300))] rounded-2xl p-8 text-center mb-8">
            <div className="h-16 w-16 mx-auto mb-4 rounded-full bg-[hsl(var(--accent-100))] flex items-center justify-center">
              <svg className="h-8 w-8 text-[hsl(var(--accent-600))]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-[hsl(var(--accent-700))] mb-2">
              Pagamento Confirmado!
            </h2>
            <p className="text-[hsl(var(--accent-600))]">
              Esta fatura já foi paga. Obrigado!
            </p>
          </div>
        ) : (
          <>
            {/* Invoice & Customer Info */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {/* Invoice Data */}
              <div className="bg-card rounded-2xl shadow-sm border p-6">
                <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-4">
                  Dados da Fatura
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Valor</p>
                      <p className="text-2xl font-bold text-[hsl(var(--basic-900))]">
                        {formatCurrency(invoice.amount)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Vencimento</p>
                      <p className="text-lg font-bold text-[hsl(var(--basic-900))]">
                        {new Date(invoice.due_date).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Juros</p>
                      <p className="font-medium text-[hsl(var(--basic-700))]">0%</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Multa</p>
                      <p className="font-medium text-[hsl(var(--basic-700))]">0%</p>
                    </div>
                  </div>
                  {invoice.description && (
                    <div>
                      <p className="text-xs text-muted-foreground">Descrição</p>
                      <p className="text-sm text-[hsl(var(--basic-700))]">{invoice.description}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Customer Data */}
              <div className="bg-card rounded-2xl shadow-sm border p-6">
                <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-4">
                  Dados do Pagador
                </h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Nome</p>
                    <p className="font-bold text-[hsl(var(--basic-900))]">{maskNameLGPD(invoice.customer.name)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">CPF/CNPJ</p>
                    <p className="font-mono text-[hsl(var(--basic-700))]">
                      {maskTaxIdLGPD(invoice.customer.tax_id)}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Email</p>
                      <p className="text-sm text-[hsl(var(--basic-700))] truncate">
                        {maskEmailLGPD(invoice.customer.email)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Telefone</p>
                      <p className="text-sm text-[hsl(var(--basic-700))]">
                        {maskPhoneLGPD(invoice.customer.phone)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Method Selection */}
            <div className="bg-card rounded-2xl shadow-sm border p-6 mb-8">
              <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-4">
                Forma de Pagamento
              </h3>
              
              <div className="grid grid-cols-2 gap-4 mb-6">
                <button
                  onClick={() => setPaymentMethod('boleto')}
                  className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                    paymentMethod === 'boleto'
                      ? 'border-[hsl(var(--basic-500))] bg-[hsl(var(--basic-50))]'
                      : 'border-transparent bg-muted/30 hover:bg-muted/50'
                  }`}
                >
                  <FileText className={`h-8 w-8 ${
                    paymentMethod === 'boleto' ? 'text-[hsl(var(--basic-600))]' : 'text-muted-foreground'
                  }`} />
                  <span className={`font-bold ${
                    paymentMethod === 'boleto' ? 'text-[hsl(var(--basic-900))]' : 'text-muted-foreground'
                  }`}>
                    Pagar com Boleto
                  </span>
                </button>

                <button
                  onClick={() => setPaymentMethod('pix')}
                  className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                    paymentMethod === 'pix'
                      ? 'border-[hsl(var(--accent-500))] bg-[hsl(var(--accent-50))]'
                      : 'border-transparent bg-muted/30 hover:bg-muted/50'
                  }`}
                >
                  <QrCode className={`h-8 w-8 ${
                    paymentMethod === 'pix' ? 'text-[hsl(var(--accent-600))]' : 'text-muted-foreground'
                  }`} />
                  <span className={`font-bold ${
                    paymentMethod === 'pix' ? 'text-[hsl(var(--basic-900))]' : 'text-muted-foreground'
                  }`}>
                    Pagar com PIX
                  </span>
                </button>
              </div>

              {/* Payment Component */}
              {paymentMethod === 'pix' ? (
                <PixPayment 
                  pixCode={invoice.pix_code} 
                  amount={invoice.amount} 
                />
              ) : (
                <BoletoPayment 
                  boletoBarcode={invoice.boleto_barcode}
                  boletoPdfUrl={invoice.boleto_pdf_url}
                  amount={invoice.amount}
                />
              )}
            </div>
          </>
        )}
      </main>

      {/* Footer - Digisend Branding */}
      <footer className="border-t bg-card py-6">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm text-muted-foreground">
            Powered by <span className="font-bold text-[hsl(var(--basic-700))]">Digisend</span>
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Cobrança inteligente • Pagamento processado de forma segura
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PaymentPage;
